import { useState, useEffect, useCallback, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, theme, Dropdown, Space, Avatar, message } from 'antd';
import type { MenuProps } from 'antd';
import { UserOutlined, LogoutOutlined } from '@ant-design/icons';
import styles from '@/styles/MainLayout.module.css';
import { getUserMenus, Menu as MenuType } from '@/api/menu';
import { getIcon } from '@/utils/iconMap';

const { Header, Sider, Content, Footer } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

// 用户信息类型
interface UserInfo {
  user_id: number;
  username: string;
  nickname: string;
  avatar?: string;
  roles?: string[];
}

// 转换菜单数据为 Ant Design 菜单格式 (纯函数移出组件，避免重复创建)
const transformMenuData = (menus: MenuType[]): MenuItem[] => {
  return menus.map((menu) => {
    const uniqueKey = menu.path ? `${menu.path}#${menu.id}` : String(menu.id);
    const item = {
      key: uniqueKey,
      icon: getIcon(menu.icon ?? undefined),
      label: menu.menu_name,
      children:
        menu.children && menu.children.length > 0 ? transformMenuData(menu.children) : undefined,
    } as MenuItem;
    return item;
  });
};

// Find the matching key for current location (纯函数移出组件)
const findSelectedKey = (items: MenuItem[], currentPath: string): string | null => {
  for (const item of items) {
    if (!item) continue;
    if (item.key && String(item.key).split('#')[0] === currentPath) {
      return String(item.key);
    }
    if ((item as any).children) {
      const childResult = findSelectedKey((item as any).children, currentPath);
      if (childResult) return childResult;
    }
  }
  return null;
};

/**
 * 主布局组件
 * 包含顶部导航、侧边菜单和内容区域
 */
export default function MainLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  // 获取用户菜单
  const fetchUserMenus = useCallback(async () => {
    try {
      const res = await getUserMenus();
      if (res.data) {
        const items = transformMenuData(res.data);
        setMenuItems(items);
      }
    } catch (error) {
      console.error('获取菜单失败:', error);
      message.error('加载菜单失败');
    }
  }, []);

  // 检查登录状态并加载菜单
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userInfoStr = localStorage.getItem('user_info');

    if (!token) {
      navigate('/login', { replace: true });
      return;
    }

    if (userInfoStr) {
      try {
        setUserInfo(JSON.parse(userInfoStr));
      } catch (e) {
        console.error('解析用户信息失败:', e);
      }
    }

    fetchUserMenus();
  }, [navigate, fetchUserMenus]);

  // 处理退出登录
  const handleLogout = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        await fetch('/api/v1/auth/logout', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }).catch(() => {});
      }
    } catch (e) {
      console.warn('登出接口调用失败:', e);
    }

    localStorage.removeItem('token');
    localStorage.removeItem('tenant_id');
    localStorage.removeItem('user_info');
    message.success('已退出登录');
    navigate('/login', { replace: true });
  }, [navigate]);

  // 用户下拉菜单缓存
  const userMenuItems: MenuProps['items'] = useMemo(
    () => [
      {
        key: 'profile',
        icon: <UserOutlined />,
        label: '个人中心',
        onClick: () => message.info('个人中心功能开发中'),
      },
      {
        type: 'divider',
      },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: '退出登录',
        onClick: handleLogout,
      },
    ],
    [handleLogout]
  );

  // 处理菜单点击缓存
  const handleMenuClick: MenuProps['onClick'] = useCallback(
    ({ key }: Parameters<NonNullable<MenuProps['onClick']>>[0]) => {
      const path = String(key).split('#')[0];
      if (path) {
        // 确保路径以 / 开头（兼容数据库中可能没有前导斜杠的路径）
        const normalizedPath = path.startsWith('/') ? path : `/${path}`;
        navigate(normalizedPath);
      }
    },
    [navigate]
  );

  // 缓存当前选中的菜单项
  const selectedKeys = useMemo(() => {
    const key = findSelectedKey(menuItems, location.pathname);
    return key ? [key] : [];
  }, [menuItems, location.pathname]);

  // 动态获取展开的子菜单缓存
  const defaultOpenKeys = useMemo(() => {
    const path = location.pathname;
    const parts = path.split('/').filter(Boolean);
    if (parts.length > 0) {
      return [parts[0]];
    }
    return [];
  }, [location.pathname]);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* 侧边栏 */}
      <Sider collapsible collapsed={collapsed} onCollapse={setCollapsed} className={styles.sider}>
        {/* Logo */}
        <div className={styles.logo}>
          <span className={styles.logoText} style={{ fontSize: collapsed ? 14 : 16 }}>
            {collapsed ? '照护' : '长小养照护系统'}
          </span>
        </div>

        {/* 菜单 */}
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKeys}
          defaultOpenKeys={defaultOpenKeys}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>

      {/* 主内容区域 */}
      <Layout style={{ marginLeft: collapsed ? 80 : 200, transition: 'margin-left 0.2s' }}>
        {/* 顶部栏 */}
        <Header className={styles.header} style={{ background: colorBgContainer }}>
          <h2 className={styles.headerTitle}>智能长者照护计划生成系统</h2>

          {/* 用户信息区域 */}
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Space className={styles.userInfo}>
              <Avatar
                size="small"
                icon={<UserOutlined />}
                src={userInfo?.avatar}
                style={{ backgroundColor: '#1890ff' }}
              />
              <span className={styles.userName}>
                {userInfo?.nickname || userInfo?.username || '用户'}
              </span>
            </Space>
          </Dropdown>
        </Header>

        {/* 内容区域 */}
        <Content
          className={styles.content}
          style={{
            background: colorBgContainer,
            borderRadius: borderRadiusLG,
          }}
        >
          <Outlet />
        </Content>

        {/* 底部 */}
        <Footer className={styles.footer}>© 2025 海南长小养智能科技有限责任公司</Footer>
      </Layout>
    </Layout>
  );
}
